import { createBrowserRouter, useRouteError } from 'react-router-dom'
import { lazy, type ComponentType, type LazyExoticComponent } from 'react'
import App from './App'

/** Retry lazy import (handles transient "Failed to fetch dynamically imported module" from Vite/HMR) */
function lazyWithRetry<T extends ComponentType<unknown>>(
  importFn: () => Promise<{ default: T }>,
  retries = 3
): LazyExoticComponent<T> {
  return lazy(async () => {
    let lastError: unknown
    for (let i = 0; i < retries; i++) {
      try {
        return await importFn()
      } catch (e) {
        lastError = e
        if (i < retries - 1) await new Promise((r) => setTimeout(r, 500 * (i + 1)))
      }
    }
    throw lastError
  })
}

function RouteErrorFallback() {
  const error = useRouteError()
  const message = error instanceof Error ? error.message : String(error)
  return (
    <div className="flex flex-col items-center justify-center flex-1 p-8 text-center">
      <h2 className="text-xl font-semibold text-red-400 mb-2">Something went wrong</h2>
      <p className="text-gray-400 mb-4 max-w-md">{message}</p>
      <button
        onClick={() => window.location.reload()}
        className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded text-white"
      >
        Reload page
      </button>
    </div>
  )
}

// Lazy load pages (with retry for transient import failures)
const MapEditorPage = lazyWithRetry(() => import('./pages/MapEditorPage'))
const InventoryPage = lazyWithRetry(() => import('./pages/InventoryPage'))
const SkillerPage = lazyWithRetry(() => import('./pages/SkillerPage'))

export const router = createBrowserRouter([
  {
    path: '/',
    element: <App />,
    errorElement: (
      <div className="h-screen bg-gray-900 text-white flex items-center justify-center">
        <RouteErrorFallback />
      </div>
    ),
    children: [
      {
        index: true,
        element: <MapEditorPage />,
      },
      {
        path: 'assets',
        element: <MapEditorPage />,
      },
      {
        path: 'browser',
        element: <MapEditorPage />,
      },
      {
        path: 'inventory',
        element: <InventoryPage />,
        errorElement: <RouteErrorFallback />,
      },
      {
        path: 'skiller',
        element: <SkillerPage />,
      },
      {
        path: '*',
        element: (
          <div className="flex items-center justify-center h-screen">
            <div className="text-center">
              <h1 className="text-4xl font-bold mb-4">404</h1>
              <p className="text-gray-400 mb-4">Page not found</p>
              <a href="/" className="text-purple-500 hover:underline">
                Go home
              </a>
            </div>
          </div>
        ),
      },
    ],
  },
])
